import { jsx, jsxs } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Box, Stack, Heading, Loader, useResponsiveValue, Card, IconRecommended, Columns, Column, Inline, Text, List, IconTick } from "braid-design-system";
import * as React from "react";
import { useContext, createContext, useRef, useEffect, useCallback } from "react";
import * as Apollo from "@apollo/client";
import { gql } from "@apollo/client";
import { formatMinorUnits } from "@seek/currency";
import { translations } from "../../../../HirerOrderSummary/components/.vocab/index.mjs";
import { priceSummary } from "./CPQASummary.css.mjs";
const defaultOptions = {};
const MoneyFieldsFragmentDoc = gql`
    fragment MoneyFields on Money {
  value
  currency
}
    `;
const ContractValueFieldsFragmentDoc = gql`
    fragment ContractValueFields on ContractValue {
  dollars {
    remaining {
      ...MoneyFields
    }
    original {
      ...MoneyFields
    }
  }
  units {
    remaining
    original
  }
}
    ${MoneyFieldsFragmentDoc}`;
const AccountContextDocument = gql`
    query AccountContext {
  currentAdvertiser {
    ... on CurrentAdvertiserContext {
      __typename
      name
      seekId
      advertiserId
      isAgency
      billingId
      claims {
        type
        value
        __typename
      }
      permissionOptions {
        canCreateJobs
        canManageBrand
        canManageCompanyProfile
        canManageUsers
        canPayInvoices
        canViewInvoiceHistory
        canViewManagerReports
        __typename
      }
      status
      role
      talentSearchAccess
      profileProperties {
        ExpressCajaOptOut
        HasLoggedInBefore
        HasOnlyUsedExpressCaja
        HasSeenGuidedCallOut
        HasSeenJobDetailsTour
        UserStatusBeforeDeactivation
        __typename
      }
    }
    ... on Error {
      __typename
      message
    }
    __typename
  }
  currentUser {
    ... on CurrentUserContext {
      __typename
      id
      email
      firstName
      lastName
      name
      status
    }
    ... on Error {
      __typename
      message
    }
    __typename
  }
  headerFooterContext {
    identityContext {
      email
      act {
        operatorEmail
        isCsView
        __typename
      }
      __typename
    }
    user {
      name
      firstName
      id
      __typename
    }
    advertiser {
      name
      billingId
      hasMultipleAccounts
      permissionOptions {
        canCreateJobs
        canManageBrand
        canManageCompanyProfile
        canManageUsers
        canPayInvoices
        canViewInvoiceHistory
        canViewManagerReports
        __typename
      }
      showOnCreditHoldMessage
      isActivationPending
      switchAccountsHref
      isAgency
      __typename
    }
    __typename
  }
}
    `;
function useAccountContextQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(AccountContextDocument, options);
}
function useAccountContextLazyQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery(AccountContextDocument, options);
}
function useAccountContextSuspenseQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery(AccountContextDocument, options);
}
const GetContractBalancesDocument = gql`
    query GetContractBalances($advertiserNation: String!) {
  contractBalances(advertiserNation: $advertiserNation) {
    totalValue {
      dollars {
        remaining {
          value
        }
      }
    }
  }
}
    `;
function useGetContractBalancesQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(GetContractBalancesDocument, options);
}
function useGetContractBalancesLazyQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery(GetContractBalancesDocument, options);
}
function useGetContractBalancesSuspenseQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery(GetContractBalancesDocument, options);
}
const HirerAccountDocument = gql`
    query HirerAccount {
  hirerAccount {
    billingStatus
    hasAgent
    hidePrice
    nation
  }
}
    `;
function useHirerAccountQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(HirerAccountDocument, options);
}
function useHirerAccountLazyQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery(HirerAccountDocument, options);
}
function useHirerAccountSuspenseQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery(HirerAccountDocument, options);
}
const GetOrderSummaryDocument = gql`
    query GetOrderSummary($orderId: ID!, $advertiserNation: String!) {
  orderSummary(orderId: $orderId, advertiserNation: $advertiserNation) {
    lineItems {
      price {
        netPrice {
          value
        }
      }
      pricing {
        __typename
      }
    }
    total {
      value
      currency
    }
  }
}
    `;
function useGetOrderSummaryQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(GetOrderSummaryDocument, options);
}
function useGetOrderSummaryLazyQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery(GetOrderSummaryDocument, options);
}
function useGetOrderSummarySuspenseQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery(GetOrderSummaryDocument, options);
}
const ContractBalancesForHirerBalanceDocument = gql`
    query ContractBalancesForHirerBalance($advertiserNation: String!) {
  contractBalances(advertiserNation: $advertiserNation) {
    hideBalance
    title
    contractType
    dollarBased
    adType
    adTypeDescription
    contracts {
      expiry {
        expiryDate
        expiresSoon
        daysRemaining
      }
      createdDate
      validFromDate
      value {
        ...ContractValueFields
      }
      current
    }
    totalValue {
      ...ContractValueFields
    }
    lowBalance
    recommendUpSell
  }
}
    ${ContractValueFieldsFragmentDoc}`;
function useContractBalancesForHirerBalanceQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(ContractBalancesForHirerBalanceDocument, options);
}
function useContractBalancesForHirerBalanceLazyQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery(ContractBalancesForHirerBalanceDocument, options);
}
function useContractBalancesForHirerBalanceSuspenseQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery(ContractBalancesForHirerBalanceDocument, options);
}
const HirerAccountForHirerBalanceDocument = gql`
    query HirerAccountForHirerBalance {
  hirerAccount {
    pricingModel
    __typename
  }
}
    `;
function useHirerAccountForHirerBalanceQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(HirerAccountForHirerBalanceDocument, options);
}
function useHirerAccountForHirerBalanceLazyQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery(HirerAccountForHirerBalanceDocument, options);
}
function useHirerAccountForHirerBalanceSuspenseQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery(HirerAccountForHirerBalanceDocument, options);
}
const OrderSummaryDetailsDocument = gql`
    query OrderSummaryDetails($orderId: ID!, $advertiserId: ID, $advertiserNation: String!) {
  orderSummary(
    orderId: $orderId
    advertiserId: $advertiserId
    advertiserNation: $advertiserNation
  ) {
    orderId
    tax {
      rate
      amount {
        value
        currency
      }
      code
      displayRecommendation
    }
    subTotal {
      value
      currency
    }
    total {
      value
      currency
    }
    lineItems {
      product {
        __typename
        productType
        name
        ... on Ad {
          adType
        }
      }
      price {
        retailPrice {
          value
          currency
        }
        netPrice {
          value
          currency
        }
      }
      pricing {
        ... on CappedUnitPrice {
          unitPrice {
            ... on Price {
              netPrice {
                value
                currency
              }
              tax {
                rate
                code
                displayRecommendation
                amount {
                  value
                }
              }
            }
          }
          cappedPrice {
            ... on Price {
              netPrice {
                value
                currency
              }
              tax {
                rate
                code
                displayRecommendation
                amount {
                  value
                }
              }
            }
          }
        }
      }
      drawdownItems {
        amount
        drawdownContractDetails {
          contractId
          contractName
          contractType
          adType
          dollarBased
          expiryDate
          currentValue {
            dollars {
              value
              currency
            }
          }
          remainingValue {
            dollars {
              value
              currency
            }
          }
        }
      }
    }
    surcharges {
      surchargeType
      amount {
        value
        currency
      }
    }
  }
}
    `;
function useOrderSummaryDetailsQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(OrderSummaryDetailsDocument, options);
}
function useOrderSummaryDetailsLazyQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery(OrderSummaryDetailsDocument, options);
}
function useOrderSummaryDetailsSuspenseQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery(OrderSummaryDetailsDocument, options);
}
const CreateOrderWithPromotionDocument = gql`
    mutation CreateOrderWithPromotion($input: CreateOrderInput!) {
  createOrderWithPromotion(input: $input) {
    ... on PromotionInvalidError {
      message
    }
    ... on CreateOrderSuccessPayload {
      order {
        orderId
        orderNumber
      }
    }
  }
}
    `;
function useCreateOrderWithPromotionMutation(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation(CreateOrderWithPromotionDocument, options);
}
const defaultLogger = {
  /* eslint-disable no-console */
  warn: console.warn,
  error: console.error,
  info: console.info
  /* eslint-enable no-console */
};
const LoggerContext = React.createContext(defaultLogger);
const LoggerProvider = ({ children, context }) => /* @__PURE__ */ jsx(
  LoggerContext.Provider,
  {
    value: {
      ...defaultLogger,
      ...context
    },
    children
  }
);
const useLogger = () => {
  const { warn, error, info } = useContext(LoggerContext);
  return { warn, error, info };
};
const OrderSummaryLayout = ({
  children,
  paddingStyles,
  title
}) => /* @__PURE__ */ jsx(Box, { background: "surface", ...paddingStyles, children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
  Boolean(title) && /* @__PURE__ */ jsx(Title, { title: title || "" }),
  /* @__PURE__ */ jsx(Box, { children })
] }) });
const Title = ({ title }) => /* @__PURE__ */ jsx(Heading, { level: "3", weight: "regular", children: title });
function assert(condition, msg) {
  if (!condition) {
    throw new Error(msg);
  }
}
const alertMessageResponses = {
  costThisInvoice: "the cost of this...invoice",
  costTheOutstandingBalance: "the cost of the...outstanding amount",
  weWillDeduct: "we will deduct..."
};
const getAmexSurchargeAmount = (orderSummary, surchargeToApply) => {
  var _a, _b;
  return ((_b = (_a = orderSummary.surcharges.find(
    (surcharge) => surcharge.surchargeType === surchargeToApply
  )) == null ? void 0 : _a.amount) == null ? void 0 : _b.value) ?? 0;
};
const isUpsellProductLineItem = (lineItem) => lineItem.product.productType === "AD_BUDGET";
const isLineItemFullyDrawdown = (lineItem) => {
  var _a;
  return (((_a = lineItem.price.netPrice) == null ? void 0 : _a.value) ?? 0) === lineItem.drawdownItems.reduce((aggr, drawdown) => aggr + drawdown.amount, 0);
};
const getNetPriceFromLineItem = (lineItem) => {
  var _a;
  return ((_a = lineItem.price.netPrice) == null ? void 0 : _a.value) ?? 0;
};
const mapLineItem = (lineItem) => {
  var _a;
  const netPrice = getNetPriceFromLineItem(lineItem);
  function getTitle(item) {
    if (item.product.productType === "JOB_AD_EDIT") {
      return "Ad edits";
    }
    if (item.product.productType === "JOB_AD_UPGRADE") {
      return "Upgrade to StandOut ad";
    }
    return lineItem.product.name ?? "";
  }
  return {
    state: "fullPrice",
    netPrice,
    productType: lineItem.product.productType,
    retailPrice: ((_a = lineItem.price.retailPrice) == null ? void 0 : _a.value) ?? 0,
    title: getTitle(lineItem),
    currency: lineItem.price.retailPrice.currency
  };
};
const getOutstandingAmount = (lineItem, amount) => (lineItem.state === "fullPrice" ? lineItem.netPrice : lineItem.outstandingAmount) - amount;
const getPayableLineItem = (lineItem, amount, contractName) => ({
  title: lineItem.title,
  productType: lineItem.productType,
  state: "outstanding",
  paidContractName: contractName,
  currency: lineItem.currency,
  outstandingAmount: getOutstandingAmount(lineItem, amount)
});
const getKeyFromDrawdown = (drawdown) => drawdown.drawdownContractDetails.contractId ?? drawdown.drawdownContractDetails.expiryDate ?? "";
const mapDrawdownItem = (lineItem, drawdown, isUpsell) => {
  var _a, _b, _c;
  const {
    drawdownContractDetails: {
      remainingValue,
      currentValue,
      expiryDate,
      contractName,
      contractType
    }
  } = drawdown;
  return {
    key: getKeyFromDrawdown(drawdown),
    lineItems: [lineItem],
    contractType,
    contractName: contractName ?? contractType,
    remainingBalance: ((_a = remainingValue == null ? void 0 : remainingValue.dollars) == null ? void 0 : _a.value) ?? 0,
    startingBalance: ((_b = currentValue == null ? void 0 : currentValue.dollars) == null ? void 0 : _b.value) ?? 0,
    currency: ((_c = currentValue == null ? void 0 : currentValue.dollars) == null ? void 0 : _c.currency) ?? "",
    outstandingBalance: 0,
    ...expiryDate && { expiryDate },
    ...isUpsell && {
      alertMessage: alertMessageResponses.weWillDeduct
    }
  };
};
const filterOutZeroDrawdowns = (contractBalanceItem) => contractBalanceItem.startingBalance - contractBalanceItem.remainingBalance > 0;
const fillOutstandingInBalanceItem = (contractBalanceItem) => {
  const totalCost = contractBalanceItem.lineItems.reduce(
    (total, lineItem) => total + (lineItem.state === "outstanding" ? lineItem.outstandingAmount : lineItem.netPrice),
    0
  );
  const outstandingBalance = Math.max(
    0,
    totalCost - contractBalanceItem.startingBalance
  );
  assert(
    contractBalanceItem.startingBalance - contractBalanceItem.remainingBalance === totalCost - outstandingBalance,
    `contractBalance item calculation is not verified, contractBalanceItem: ${JSON.stringify(
      contractBalanceItem
    )}, totalCost: ${totalCost}, outstandingBalance: ${outstandingBalance}`
  );
  return { ...contractBalanceItem, outstandingBalance };
};
const fillExhaustAlertInBalanceItem = (contractBalanceItem, _index, contractBalanceItems) => {
  const hasOutstandingAmountLineItem = contractBalanceItem.lineItems.some(
    (lineItem) => lineItem.state === "outstanding"
  );
  if (!hasOutstandingAmountLineItem) {
    return contractBalanceItem;
  }
  const firstContractBalanceItem = contractBalanceItems.find(
    (budgetItem) => budgetItem.key !== contractBalanceItem.key
  );
  assert(
    firstContractBalanceItem,
    "It is not possible to have only one contract to drawdown a paying line item"
  );
  return {
    ...contractBalanceItem,
    ...hasOutstandingAmountLineItem && {
      alertMessage: alertMessageResponses.costTheOutstandingBalance
    }
  };
};
const getAndGroupContractBalanceItems = (lineItems, isUpsell, mapSingleLineItem) => {
  const contractBalanceItems = lineItems.reduce((drawdowns, lineItem) => {
    let contractBalanceItemsToPresent = drawdowns;
    let lineItemPresentation = mapSingleLineItem(lineItem);
    lineItem.drawdownItems.forEach((drawdown) => {
      var _a, _b, _c, _d;
      const key = getKeyFromDrawdown(drawdown);
      const existedDrawdown = drawdowns.find(
        (drawdownItem) => drawdownItem.key === key
      );
      if (existedDrawdown) {
        existedDrawdown.startingBalance = Math.max(
          existedDrawdown.startingBalance,
          ((_b = (_a = drawdown.drawdownContractDetails.currentValue) == null ? void 0 : _a.dollars) == null ? void 0 : _b.value) ?? 0
        );
        existedDrawdown.remainingBalance = Math.min(
          existedDrawdown.remainingBalance,
          ((_d = (_c = drawdown.drawdownContractDetails.remainingValue) == null ? void 0 : _c.dollars) == null ? void 0 : _d.value) ?? 0
        );
        existedDrawdown.lineItems.push(lineItemPresentation);
      } else {
        contractBalanceItemsToPresent = [
          ...contractBalanceItemsToPresent,
          mapDrawdownItem(lineItemPresentation, drawdown, isUpsell)
        ];
      }
      lineItemPresentation = getPayableLineItem(
        lineItemPresentation,
        drawdown.amount,
        drawdown.drawdownContractDetails.contractName ?? drawdown.drawdownContractDetails.contractType
      );
    });
    return contractBalanceItemsToPresent;
  }, []);
  return contractBalanceItems.filter(filterOutZeroDrawdowns).map(fillOutstandingInBalanceItem).map(fillExhaustAlertInBalanceItem);
};
function getPaidAmountAndContractFromLineItems(lineItem) {
  return lineItem.drawdownItems.reduce(
    ([total], drawdown) => [
      total + drawdown.amount,
      drawdown.drawdownContractDetails.contractName ?? drawdown.drawdownContractDetails.contractType
    ],
    [0, ""]
  );
}
const getPayableItems = (lineItems, mapSingleLineItem) => lineItems.map((lineItem) => {
  const lineItemPresentation = mapSingleLineItem(lineItem);
  const [paid, contractName] = getPaidAmountAndContractFromLineItems(lineItem);
  return paid > 0 ? getPayableLineItem(lineItemPresentation, paid, contractName) : lineItemPresentation;
}).filter(
  (lineItem) => lineItem.state === "fullPrice" || lineItem.outstandingAmount !== 0
);
const mapOrderSummaryToOrderSummaryPresentation = (orderSummary, surchargeToApply) => {
  assert(orderSummary != null, "orderSummary is null");
  const amexSurchargeAmount = getAmexSurchargeAmount(
    orderSummary,
    surchargeToApply
  );
  const total = (orderSummary.total.value ?? 0) + amexSurchargeAmount;
  const upsellLineItems = orderSummary.lineItems.filter(
    isUpsellProductLineItem
  );
  const nonUpsellLineItems = orderSummary.lineItems.filter(
    (lineItem) => !isUpsellProductLineItem(lineItem)
  );
  assert(
    upsellLineItems.length <= 1,
    "When upsell, there should be only 1 line item containing upsell product"
  );
  assert(
    upsellLineItems.length === 0 || nonUpsellLineItems.length <= 1,
    "When upsell, there should be at most one Ad in line items"
  );
  assert(
    upsellLineItems.length === 0 || nonUpsellLineItems.filter(isLineItemFullyDrawdown).length === nonUpsellLineItems.length,
    "When upsell, the Ad line items should be fully drawn down"
  );
  const lineItems = upsellLineItems.length > 0 ? upsellLineItems : orderSummary.lineItems;
  const contractBalanceItems = getAndGroupContractBalanceItems(
    upsellLineItems.length > 0 ? nonUpsellLineItems : lineItems,
    upsellLineItems.length > 0,
    mapLineItem
  );
  return {
    tax: orderSummary.tax,
    subTotal: orderSummary.subTotal.value ?? 0,
    total,
    surcharge: amexSurchargeAmount,
    contractBalanceItems,
    payableLineItems: getPayableItems(lineItems, mapLineItem),
    currency: orderSummary.total.currency
  };
};
const mapCPQAOrderSummaryToCPQAOrderSummaryPresentation = (orderSummary, locale) => {
  var _a, _b, _c, _d, _e;
  assert(orderSummary != null, "orderSummary is null");
  const cpqaLineItem = orderSummary.lineItems.find(
    (item) => {
      var _a2;
      return ((_a2 = item.pricing) == null ? void 0 : _a2.__typename) === "CappedUnitPrice";
    }
  );
  assert(
    ((_a = cpqaLineItem == null ? void 0 : cpqaLineItem.pricing) == null ? void 0 : _a.__typename) === "CappedUnitPrice" && ((_b = cpqaLineItem == null ? void 0 : cpqaLineItem.pricing) == null ? void 0 : _b.unitPrice.__typename) === "Price" && ((_c = cpqaLineItem == null ? void 0 : cpqaLineItem.pricing) == null ? void 0 : _c.cappedPrice.__typename) === "Price",
    "lineItem with pricing is not in expected shape"
  );
  const {
    pricing: {
      unitPrice: {
        netPrice: { value: unitNetPriceValue, currency },
        tax: unitPriceTax
      },
      cappedPrice: {
        netPrice: { value: cappedNetPriceValue },
        tax: cappedPriceTax
      }
    }
  } = cpqaLineItem;
  const unitPrice = (unitPriceTax == null ? void 0 : unitPriceTax.displayRecommendation) === "inclusive" ? unitNetPriceValue + (((_d = unitPriceTax == null ? void 0 : unitPriceTax.amount) == null ? void 0 : _d.value) ?? 0) : unitNetPriceValue;
  const cappedPrice = (cappedPriceTax == null ? void 0 : cappedPriceTax.displayRecommendation) === "inclusive" ? cappedNetPriceValue + (((_e = cappedPriceTax == null ? void 0 : cappedPriceTax.amount) == null ? void 0 : _e.value) ?? 0) : cappedNetPriceValue;
  const formattedCostPerApplicantPrice = formatMinorUnits(unitPrice, {
    currency,
    locale,
    dropFraction: true
  });
  const formattedCappedPrice = formatMinorUnits(cappedPrice, {
    currency,
    locale,
    dropFraction: true
  });
  return {
    costPerApplicantPrice: formattedCostPerApplicantPrice,
    tax: unitPriceTax ?? void 0,
    cappedPrice: formattedCappedPrice
  };
};
const OrderSummaryDataContext = createContext({
  hubbleTrackEventHandler: () => {
  },
  orderSummary: null,
  styles: {},
  language: "en-AU"
});
const OrderSummaryDataProvider = (props) => {
  const {
    orderId,
    advertiserId,
    advertiserNation,
    surchargeToApply,
    onLoading = () => {
    },
    styles = {},
    children,
    hubbleTrackEventHandler = () => {
    },
    language
  } = props;
  const logger = useLogger();
  const {
    loading: orderSummaryLoading,
    data,
    error
  } = useOrderSummaryDetailsQuery({
    variables: { orderId, advertiserId, advertiserNation },
    fetchPolicy: "no-cache"
  });
  const loadingPreviousRef = useRef();
  useEffect(() => {
    const currentLoading = orderSummaryLoading || !data;
    if (currentLoading !== loadingPreviousRef.current) {
      onLoading(!loadingPreviousRef.current);
      loadingPreviousRef.current = !loadingPreviousRef.current;
    }
  }, [orderSummaryLoading, onLoading, data]);
  const WIDGET_NAME = "OrderSummary";
  const hubbleTrackEventHandlerWithBaseData = useCallback(
    (eventName, eventData) => hubbleTrackEventHandler(eventName, {
      ...eventData,
      // @ts-expect-error Should probably be added as an optional property on the event data type
      widgetName: WIDGET_NAME
    }),
    [hubbleTrackEventHandler]
  );
  if (error) {
    throw error;
  }
  if (orderSummaryLoading || !data) {
    if (!orderSummaryLoading) {
      logger.error(
        `Warning: OrderSummaryDetails loaded but the data is null: (orderId: ${orderId}, advertiserId: ${advertiserId})`,
        {}
      );
    }
    return /* @__PURE__ */ jsx(OrderSummaryLayout, { paddingStyles: styles.padding, children: /* @__PURE__ */ jsx(Box, { role: "loader", children: /* @__PURE__ */ jsx(Loader, { size: "small", delayVisibility: true }) }) });
  }
  if (!data.orderSummary) {
    throw new Error(
      `Order (orderId: ${orderId}, advertiserId: ${advertiserId}) does not exist.`
    );
  }
  return /* @__PURE__ */ jsx(
    OrderSummaryDataContext.Provider,
    {
      value: {
        hubbleTrackEventHandler: hubbleTrackEventHandlerWithBaseData,
        orderSummary: data.orderSummary,
        surchargeToApply,
        styles,
        language
      },
      children
    }
  );
};
const useOrderSummaryPresentationData = () => {
  const {
    orderSummary,
    surchargeToApply,
    styles: { padding, space },
    language
  } = useContext(OrderSummaryDataContext);
  assert(
    orderSummary,
    "orderSummary must be defined in the OrderSummaryDataProvider"
  );
  const orderSummaryPresentation = mapOrderSummaryToOrderSummaryPresentation(
    orderSummary,
    surchargeToApply
  );
  const isCPQASummary = Boolean(
    orderSummary.lineItems.find(
      (item) => {
        var _a;
        return ((_a = item.pricing) == null ? void 0 : _a.__typename) === "CappedUnitPrice";
      }
    )
  );
  const cpqaSummaryPresentation = isCPQASummary ? mapCPQAOrderSummaryToCPQAOrderSummaryPresentation(orderSummary, language) : void 0;
  return {
    orderSummary: orderSummaryPresentation,
    padding,
    space,
    language,
    cpqaOrderSummary: cpqaSummaryPresentation
  };
};
const CPQASummary = () => {
  var _a, _b, _c;
  const { t } = useTranslations(translations);
  const { cpqaOrderSummary } = useOrderSummaryPresentationData();
  assert(cpqaOrderSummary, "CPQA order summary should be defined");
  const { hubbleTrackEventHandler, orderSummary: contextOrderSummary } = useContext(OrderSummaryDataContext);
  const orderId = contextOrderSummary == null ? void 0 : contextOrderSummary.orderId;
  useEffect(() => {
    if (orderId) {
      hubbleTrackEventHandler("order_summary_displayed", {
        orderId
      });
    }
  }, [orderId, hubbleTrackEventHandler]);
  const { costPerApplicantPrice, cappedPrice } = cpqaOrderSummary;
  const isMobile = useResponsiveValue()({
    mobile: true,
    desktop: false,
    tablet: false,
    wide: false
  });
  const isTaxFree = !Boolean(cpqaOrderSummary == null ? void 0 : cpqaOrderSummary.tax) || ((_a = cpqaOrderSummary == null ? void 0 : cpqaOrderSummary.tax) == null ? void 0 : _a.rate) === 0;
  const taxCode = ((_b = cpqaOrderSummary == null ? void 0 : cpqaOrderSummary.tax) == null ? void 0 : _b.code) ?? "GST";
  const taxDisplayRecommendation = ((_c = cpqaOrderSummary == null ? void 0 : cpqaOrderSummary.tax) == null ? void 0 : _c.displayRecommendation) ?? "exclusive";
  const taxPrefix = taxDisplayRecommendation === "inclusive" ? t("taxPrefixInclusive") : t("taxPrefixExclusive");
  return /* @__PURE__ */ jsx(Card, { rounded: true, children: /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
    /* @__PURE__ */ jsx(Heading, { level: "3", weight: "weak", icon: /* @__PURE__ */ jsx(IconRecommended, {}), children: "Pay Per Application" }),
    /* @__PURE__ */ jsx(Stack, { space: "medium", children: /* @__PURE__ */ jsxs(Columns, { space: "small", collapseBelow: "tablet", alignY: "center", children: [
      /* @__PURE__ */ jsx(Column, { width: "1/2", children: /* @__PURE__ */ jsx(
        Box,
        {
          paddingX: "medium",
          paddingY: "gutter",
          borderRadius: "standard",
          background: "formAccentSoft",
          className: priceSummary,
          children: /* @__PURE__ */ jsxs(Stack, { space: "small", align: "left", children: [
            /* @__PURE__ */ jsx(Inline, { alignY: "bottom", space: "xxsmall", children: /* @__PURE__ */ jsx(
              Heading,
              {
                level: "2",
                weight: "regular",
                data: { testid: "costPerApplicant" },
                children: costPerApplicantPrice
              }
            ) }),
            /* @__PURE__ */ jsx(
              Box,
              {
                marginTop: "xxsmall",
                marginBottom: isMobile ? "medium" : "xxsmall",
                children: isTaxFree ? /* @__PURE__ */ jsx(
                  Text,
                  {
                    size: isMobile ? "large" : "small",
                    tone: "secondary",
                    children: t("/application")
                  }
                ) : /* @__PURE__ */ jsx(
                  Text,
                  {
                    size: isMobile ? "large" : "small",
                    tone: "secondary",
                    children: t("GST/application", {
                      taxCode,
                      taxPrefix
                    })
                  }
                )
              }
            ),
            /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
              /* @__PURE__ */ jsxs(Text, { tone: "secondary", children: [
                t("Capped at", {
                  cappedPrice
                }),
                !isTaxFree && ` ${taxPrefix} ${taxCode}`
              ] }),
              /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("Only pay for applications you get") })
            ] })
          ] })
        }
      ) }),
      /* @__PURE__ */ jsx(Box, { marginRight: "gutter", marginBottom: "gutter" }),
      /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        /* @__PURE__ */ jsx(Text, { weight: "medium", children: t("Only pay for applications that claim") }),
        /* @__PURE__ */ jsx(List, { type: "icon", icon: /* @__PURE__ */ jsx(IconTick, {}), children: /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("the right to work in Australia") }) }),
        /* @__PURE__ */ jsx(List, { type: "icon", icon: /* @__PURE__ */ jsx(IconTick, {}), children: /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("at least 2 years’ experience in your advertised role") }) })
      ] }) })
    ] }) })
  ] }) });
};
export {
  AccountContextDocument,
  CPQASummary,
  ContractBalancesForHirerBalanceDocument,
  ContractValueFieldsFragmentDoc,
  CreateOrderWithPromotionDocument,
  GetContractBalancesDocument,
  GetOrderSummaryDocument,
  HirerAccountDocument,
  HirerAccountForHirerBalanceDocument,
  LoggerProvider,
  MoneyFieldsFragmentDoc,
  OrderSummaryDataContext,
  OrderSummaryDataProvider,
  OrderSummaryDetailsDocument,
  OrderSummaryLayout,
  alertMessageResponses,
  assert,
  useAccountContextLazyQuery,
  useAccountContextQuery,
  useAccountContextSuspenseQuery,
  useContractBalancesForHirerBalanceLazyQuery,
  useContractBalancesForHirerBalanceQuery,
  useContractBalancesForHirerBalanceSuspenseQuery,
  useCreateOrderWithPromotionMutation,
  useGetContractBalancesLazyQuery,
  useGetContractBalancesQuery,
  useGetContractBalancesSuspenseQuery,
  useGetOrderSummaryLazyQuery,
  useGetOrderSummaryQuery,
  useGetOrderSummarySuspenseQuery,
  useHirerAccountForHirerBalanceLazyQuery,
  useHirerAccountForHirerBalanceQuery,
  useHirerAccountForHirerBalanceSuspenseQuery,
  useHirerAccountLazyQuery,
  useHirerAccountQuery,
  useHirerAccountSuspenseQuery,
  useOrderSummaryDetailsLazyQuery,
  useOrderSummaryDetailsQuery,
  useOrderSummaryDetailsSuspenseQuery,
  useOrderSummaryPresentationData
};
